import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breakpoint from '../../Breakpoint'
import Loader from '../../components/global/Loader'
import SearchInput from '../../components/global/SearchInput'
import SkillCard from '../../components/global/SkillCard'
import Title from '../../components/global/Title'
import FilterStatus from '../../components/member/FilterStatus'
import UserContext from '../../context/UserContext'
import { useLangSelected } from '../../hooks/useLangSelected'
import apiClient from '../../services/apiClient'
import { SkillStatusEnum, UserSkill } from '../../types/skill.type'
import { UserModel } from '../../types/user.type'
import { SkillsWrapper } from '../coach/my-members/CoachMyMembers'

const MemberMyProgression = () => {
    const user = useContext(UserContext)
    const { t } = useTranslation('translation')
    const navigate = useNavigate()
    const lng = useLangSelected()

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedStatus, setSelectedStatus] = useState<SkillStatusEnum[]>([
        SkillStatusEnum.TO_DISCOVER,
        SkillStatusEnum.STARTED,
        SkillStatusEnum.CONFIRMED,
        SkillStatusEnum.MASTERED,
    ])

    const { data: currentUser, refetch: refetchMySkills } = useQuery<UserModel>({
        queryKey: ['currentUser'],
        queryFn: () =>
            apiClient
                .get<UserModel>(`/users/${user.memberData.id}`, { params: { role: 'ROLE_MEMBER' } })
                .then((res) => res.data),
    })

    const skillsFiltered = useMemo(
        () =>
            currentUser?.skills
                ?.filter((skill: UserSkill) => `${skill.skill.name}`.toLowerCase().includes(searchTerm.toLowerCase()))
                .filter((skill: UserSkill) => selectedStatus.includes(skill.status)) ?? [],
        [searchTerm, currentUser, selectedStatus],
    )

    if (!currentUser) return <Loader />

    return (
        <>
            <Title>{t('member.my-progression')}</Title>

            <NotificationPreference>
                <label>
                    <input
                        type="checkbox"
                        checked={currentUser.notifySkillChanges}
                        onChange={async (e) => {
                            try {
                                await apiClient.patch(`/users/${user.memberData.id}`, {
                                    notifySkillChanges: e.target.checked,
                                })
                                await refetchMySkills()
                                toast.success(t('member.notification-preference-updated'))
                            } catch (error) {
                                console.error('Failed to update notification preference:', error)
                                toast.error(t('common.error-occurred'))
                            }
                        }}
                    />
                    {t('member.notify-skill-changes')}
                </label>
            </NotificationPreference>

            <FilterWrapper>
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('member.search-skill-input')}
                />
                <FilterStatus selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
            </FilterWrapper>
            <SkillsWrapper>
                {skillsFiltered.map((skill: UserSkill) => (
                    <SkillCard
                        key={'SkillCard-' + skill.id}
                        id={skill.id}
                        name={skill.skill.name}
                        status={skill.status}
                        comment={skill.comment}
                        isFavorite={skill.isFavorite}
                        refetchMySkills={refetchMySkills}
                        redirect={() => navigate(`/${lng}/bva-membre/ma-progression/${skill.id}`)}
                    />
                ))}
            </SkillsWrapper>
        </>
    )
}

export const NotificationPreference = styled.div`
    margin: 0 0 24px 0;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    label {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        font-size: 16px;
        color: #495057;

        input[type='checkbox'] {
            width: 18px;
            height: 18px;
            cursor: pointer;
        }
    }
`

export const FilterWrapper = styled.div`
    display: flex;
    margin-bottom: 16px;
    gap: 16px;

    & > div {
        margin-bottom: 0;
        margin-right: 16px;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        flex-direction: column;
    }
`

export default MemberMyProgression
